import React, { useState, useEffect } from 'react'
import { Table } from 'reactstrap'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import { orderFileInfo, TableProps } from 'models/table.model'

import OrderModal from '../OrderModal'
import Pagination from './Pagination'

import RenderCell from 'components/RenderCell'
import HeaderTable from 'components/HeaderTable'

import API from 'utils/API'
import { alertSuccess, alertError, alertWarn } from 'utils/notifications'
import { sortChangedHandler } from 'utils/sortHandler'
import { sendReloadInfo } from 'utils/socket'
import { history } from 'store'

const ProductsTable: React.FC<TableProps> = ({
  columns,
  data,
  setSortCriteria,
  sortCriteria,
  searchValue,
  pageSize,
  getData,
  setData,
  allData,
  // Nouvelles propriétés
  hasMore = false,
  loadingMore = false,
  isFullyLoaded = false,
  loadNextBatch,
  loadAllData,
  autoLoadingTriggered = false,
  totalCount = 0,
  activeFilter = 0,
  serverPageCount,
  onRequestPage
}) => {
  // Use the state and functions returned from useTable to build your UI
  const [offset, setOffset] = useState(0)
  // En mode pagination paresseuse (serveur), le nombre de pages vient de totalCount ;
  // sinon il est calculé sur les données déjà chargées/filtrées
  const pageCount = serverPageCount ?? (data.length === 0 ? 1 : Math.ceil(data.length / (pageSize as number)))
  const dataToShow = data.slice(offset * (pageSize as number), (pageSize as number) * (offset + 1))
  const navLoading = onRequestPage != null && loadingMore === true

  // Aller à une page : demande le chargement du lot serveur si la page n'est pas encore chargée
  const goToPage = (page: number): void => {
    // Une navigation est ignorée pendant le chargement d'une page, et bornée aux pages existantes
    if (navLoading) return
    const newPage = Math.min(Math.max(page, 0), pageCount - 1)
    if (newPage === offset) return
    setOffset(newPage)
    if (onRequestPage != null) onRequestPage(newPage)
  }
  const navigatePage = (step: number): void => {
    goToPage(offset + step)
  }
  const [filesNames] = useState<orderFileInfo[][]>([])
  // Lien direct : /orders?order=<orderId>[&chat=1] ouvre la fiche (et les messages patient)
  const [linkedParams] = useState(() => new URLSearchParams(window.location.search))
  const linkedOrderId = parseInt(linkedParams.get('order') ?? '')
  const [openModalOrder, setOpenModalOrder] = useState(!isNaN(linkedOrderId))
  const [orderIdToShow, setOrderIdToShow] = useState<number>(isNaN(linkedOrderId) ? -1 : linkedOrderId)
  const [pharmacyOrderIdToShow, setPharmacyOrderIdToShow] = useState<number>(
    -1
  )
  const [selectOrderId, setSelectOrderId] = useState<number>(-1)
  const [iconCircle, setIconCircle] = useState<string>('faCircleNotch')

  const { formatMessage, locale } = useIntl()
  const f = (id: any): string => formatMessage({ id })

  useEffect(() => {
    setOffset(0)
  }, [searchValue])

  useEffect(() => {
    setOffset(0)
  }, [activeFilter])

  const displayFaCircle = (): void => {
    setTimeout(function () {
      setIconCircle('faCheckCircle')
    }, 2000)
  }
  useEffect(() => {
    /* data.forEach((elem: any, index: any) => {
      API.post("/file/getOrderInfo", {
        orderId: elem.order_id,
        pharmacyOrderId: elem.pharmacyOrderId,
      }).then((result) => {
        setFilesNames((prevState) => [...prevState, result.data.data]);
      });
    }); */
  }, [data])

  const editShipped = (
    orderId: number,
    pharmacyOrderId: number,
    type: number,
    shipped: boolean,
    pharmacyName: string,
    origin: string
  ): void => {
    // Mise à jour optimiste locale immédiate pour un feedback instantané
    setSelectOrderId(orderId)
    setIconCircle('faCircleNotch')

    API.post('pharmacist/carts/shipped', {
      orderId,
      pharmacyOrderId,
      type,
      shipped,
      pharmacyName,
      origin
    })
      .then((res) => {
        if (res.data.success) {
          setIconCircle('faCheckCircle')
          
          // Mise à jour locale uniquement de la commande concernée
          if (setData && allData) {
            setData(prevData => 
              prevData.map(order => 
                (order.orderId === orderId && order.pharmacyOrderId === pharmacyOrderId)
                  ? { ...order, shipped: shipped }
                  : order
              )
            )
          }
          
          shipped
            ? alertSuccess(f('packageConfirm'))
            : alertWarn(f('packageCanceled'))
        }
      })
      .catch((err) => {
        console.log(err)
        setIconCircle('faCheckCircle')
        alertError(f('failedUpdate'))
      })
  }

  // Un clic sur une ligne pose toujours les deux ids (cf. RenderCell), même quand la
  // modale ne doit pas s'ouvrir (texte sélectionné). Sans remise à -1, OrderModal
  // restait monté sans être visible, et plantait dès que la commande sortait des
  // lignes filtrées (recherche, changement de filtre, rechargement).
  const checkTextSelected = (): void => {
    const selection = window.getSelection()
    const shouldOpen =
      selection !== null &&
      typeof selection.anchorNode?.nodeValue === 'string' &&
      selection.toString().length <= 0

    if (shouldOpen) {
      setOpenModalOrder(true)
    } else {
      setPharmacyOrderIdToShow(-1)
      setOrderIdToShow(-1)
    }
  }

  // Recherche dans toutes les commandes chargées (une commande ouverte par lien peut être
  // hors du filtre courant). Via un lien, seul l'orderId est connu (pharmacyOrderId à -1).
  const orderToShow = (allData ?? data).find((order: any) => (
    (pharmacyOrderIdToShow === -1 || order.pharmacyOrderId === pharmacyOrderIdToShow) &&
    order.orderId === orderIdToShow
  ))

  const setUrlParam = (key: string, value: string | null): void => {
    const searchParams = new URLSearchParams(window.location.search)
    if (value == null) searchParams.delete(key)
    else searchParams.set(key, value)
    history.replace({ search: searchParams.toString() })
  }

  useEffect(() => {
    if (openModalOrder && orderToShow != null) setUrlParam('order', orderToShow.orderId.toString())
  }, [openModalOrder, orderToShow?.orderId])

  // Render the UI for your table
  return (
    <Wrapper>
      {openModalOrder && orderToShow != null && (
        <OrderModal
          open={openModalOrder}
          filteredRows={allData ?? data}
          data={orderToShow}
          initialChatOpen={new URLSearchParams(window.location.search).get('chat') === '1'}
          onChatOpenChange={(isOpen: boolean) => setUrlParam('chat', isOpen ? '1' : null)}
          onClose={() => {
            setOpenModalOrder(false)
            setPharmacyOrderIdToShow(-1)
            setOrderIdToShow(-1)
            const searchParams = new URLSearchParams(window.location.search)
            searchParams.delete('order')
            searchParams.delete('chat')
            history.replace({ search: searchParams.toString() })
          }}
          getData={getData}
          setData={setData}
          setSelectOrderId={setSelectOrderId}
          displayFaCircle={displayFaCircle}
          setIconCircle={setIconCircle}
        />
      )}
      <StyledTable responsive size='sm'>
        <thead>
          <tr>
            <HeaderTable
              columns={columns}
              sortCriteria={sortCriteria}
              sortChangedHandler={sortChangedHandler}
              setSortCriteria={setSortCriteria}
              thEqual={false}
              thAccessor='state'
              thClassname='space'
              sortDescOrAsc='ASC'
            />
          </tr>
        </thead>
        <tbody>
          {navLoading && dataToShow.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner />
              </td>
            </tr>
          )}
          {dataToShow.map((row: any, i: number) => (
            <tr key={`orders_body_tr_${i}`}>
              {columns.map((column, j: number) => {
                return (
                  <RenderCell
                    key={`order${i}_cell${j}`}
                    row={row}
                    rowIndex={i}
                    column={column}
                    page='order'
                    filesNames={filesNames}
                    locale={locale}
                    selectOrderId={selectOrderId}
                    iconCircle={iconCircle}
                    setPharmacyOrderIdToShow={setPharmacyOrderIdToShow}
                    setOrderIdToShow={setOrderIdToShow}
                    checkTextSelected={checkTextSelected}
                    editShipped={editShipped}
                  />
                )
              })}
            </tr>
          ))}
        </tbody>
      </StyledTable>
      {/* Indicateur de chargement automatique */}
      {autoLoadingTriggered && (
        <LoadingInfo>
          <LoadingText>
            {f('autoLoading')}
          </LoadingText>
          <Spinner />
        </LoadingInfo>
      )}
      
      <Pagination
        onGoToPage={goToPage}
        onNavigate={navigatePage}
        offset={offset}
        pageCount={pageCount}
        hasMore={hasMore}
        loadingMore={loadingMore}
        isFullyLoaded={isFullyLoaded}
        loadNextBatch={loadNextBatch}
        loadAllData={loadAllData}
        autoLoadingTriggered={autoLoadingTriggered}
        loadedCount={data.length}
        totalCount={totalCount}
        searchValue={searchValue}
        activeFilter={activeFilter}
        navLoading={navLoading}
      />
    </Wrapper>
  )
}

const LoadingInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin: 10px 0;
  background-color: #f8f9fa;
  border-radius: 5px;
`

const LoadingText = styled.span`
  margin-right: 10px;
  color: var(--texte-table);
  font-weight: 600;
`

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--texte-bleu-fonce);
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

const Wrapper = styled.div`
  width: 100%;
`

const StyledTable = styled(Table)`
  background-color: white;
  width: 100%;
  thead {
    font-size: 0.9rem;
    color: var(--texte-bleu-fonce);
    th div {
      display: flex;
      align-items: center;
    }
  }

  tbody,
  tbody input {
    color: var(--texte-table);
    font-weight: 600;
    font-size: 0.9rem;
  }

  tr {
    width: 100%;
    div {
      cursor: pointer;
      display: flex;
      align-items: flex-end;
      svg {
        width: 1rem;
        margin-left: 0.5rem;
      }
    }
  }
 
  td {
    vertical-align: middle;
    padding: 0.75rem 0.5rem;
    &:focus-visible {
      background: white;
    }
  }
  tbody {
    overflow: scroll;
    max-height: 40rem;
    button {
      margin: 0.5rem 0;
    }
    svg {
      width: 1rem;
    }
  }
  th  {
    border-top: none;
    border-bottom: none !important;
    padding-bottom: 0.5rem;
   }
  .space{
    padding-left: 1.4rem ;
  }
  .minWidth{
    min-width: 11rem;
  }
`

export default ProductsTable
